import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Car, FileText, HelpCircle, AlertCircle, CheckCircle, LogOut, User, DollarSign, Download, Mail, Users, Building, Building2, ShieldCheck, Bell, AlertTriangle, Clock, Scale, Camera, UserPlus, Send, Newspaper, FileCheck, Zap, Wrench, XCircle, PenSquare, UserX, ArrowRight, RefreshCw, TrendingDown, TrendingUp, Search, X, BarChart3 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

function AdminDashboard() {
  const [dashboard, setDashboard] = useState(null);
  const [alertas, setAlertas] = useState(null);
  const [accionesManuales, setAccionesManuales] = useState(null);
  const [accionesLoading, setAccionesLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(true);
  const [enviandoReporte, setEnviandoReporte] = useState(false);
  const [bucketExpandido, setBucketExpandido] = useState(null);
  const [tendencia, setTendencia] = useState(null);
  const [tendenciaDias, setTendenciaDias] = useState(30);
  const navigate = useNavigate();
  const { usuario, loading: authLoading, logout } = useAuth();

  useEffect(() => {
    if (authLoading) return;
    if (usuario?.rol !== 'admin') {
      navigate('/');
      return;
    }
    cargarDashboard();
    cargarAlertas();
    cargarAccionesManuales();
    cargarTendencia(tendenciaDias);
  }, [authLoading, usuario]);

  useEffect(() => {
    if (!authLoading && usuario?.rol === 'admin') {
      cargarTendencia(tendenciaDias);
    }
  }, [tendenciaDias]);

  const cargarDashboard = async () => {
    try {
      const response = await api.get('/admin/dashboard');
      setDashboard(response.data.data);
    } catch (error) {
      console.error('Error cargando dashboard:', error);
      // El interceptor de api.js maneja el 401 automáticamente
    } finally {
      setDataLoading(false);
    }
  };

  const cargarAlertas = async () => {
    try {
      const response = await api.get('/alertas/multas-pendientes');
      setAlertas(response.data);
    } catch (error) {
      console.error('Error cargando alertas:', error);
    }
  };

  const cargarAccionesManuales = async () => {
    try {
      const response = await api.get('/admin/dashboard/acciones-manuales');
      setAccionesManuales(response.data.data);
    } catch (error) {
      console.error('Error cargando acciones manuales:', error);
    } finally {
      setAccionesLoading(false);
    }
  };

  const cargarTendencia = async (dias) => {
    try {
      const response = await api.get(`/admin/dashboard/tendencia?dias=${dias}`);
      setTendencia(response.data.data);
    } catch (error) {
      console.error('Error cargando tendencia:', error);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleGenerarReporte = async () => {
    try {
      const now = new Date();
      const año = now.getFullYear();
      const mes = now.getMonth() + 1;
      const response = await api.post(
        '/admin/reportes/generar',
        { año, mes },
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `reporte-multas-${año}-${mes}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error generando reporte:', error);
      alert('Error generando reporte');
    }
  };

  const handleDescargarExcelCompleto = async () => {
    try {
      const response = await api.get('/alertas/export-excel', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `reporte-completo-${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error descargando Excel:', error);
      alert('Error descargando Excel');
    }
  };

  const handleEnviarReporteAlertas = async () => {
    if (!window.confirm('¿Enviar reporte de alertas a pnieto@grupoiberica.com y lcortes@grupoiberica.com?')) return;
    setEnviandoReporte(true);
    try {
      await api.post('/alertas/enviar-reporte', {});
      alert('Reporte de alertas enviado correctamente');
    } catch (error) {
      console.error('Error enviando reporte:', error);
      alert('Error enviando reporte');
    } finally {
      setEnviandoReporte(false);
    }
  };

  const handleEnviarReporte = async () => {
    if (!window.confirm('¿Enviar reporte del mes actual a pnieto@grupoiberica.com?')) return;
    try {
      const now = new Date();
      await api.post('/admin/reportes/enviar', { año: now.getFullYear(), mes: now.getMonth() + 1 });
      alert('Reporte enviado correctamente');
    } catch (error) {
      console.error('Error enviando reporte:', error);
      alert('Error enviando reporte');
    }
  };

  if (authLoading || dataLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Cargando...</p>
        </div>
      </div>
    );
  }

  const global = dashboard?.global || {};
  const empresas = dashboard?.por_empresa || [];
  const resumen = alertas?.resumen || { criticas: 0, urgentes: 0, atencion: 0, normales: 0, total: 0 };
  const multasPendientes = alertas?.multas || [];

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white/80 backdrop-blur-lg shadow-soft border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 font-display">Dashboard Admin</h1>
              <p className="text-sm text-slate-600">{usuario.nombre} - Vista Global</p>
            </div>
            <HealthIndicator
              accionesManuales={accionesManuales}
              resumenAlertas={resumen}
              loading={accionesLoading}
            />
          </div>
          <div className="flex items-center gap-3">
            <GlobalSearch />
            <div className="flex gap-2">
              <button onClick={() => navigate("/ayuda")} className="flex items-center gap-2 px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-xl transition-colors"><HelpCircle className="w-5 h-5" />Ayuda</button>
              <button onClick={() => navigate("/admin/recursos")} className="flex items-center gap-2 px-4 py-2 bg-violet-100 text-violet-700 hover:bg-violet-200 rounded-xl transition-colors"><Scale className="w-5 h-5" />Recursos</button>
              <button onClick={() => navigate("/profile")} className="flex items-center gap-2 px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-xl transition-colors"><User className="w-5 h-5" />Perfil</button>
              <button onClick={handleLogout} className="flex items-center gap-2 px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-xl transition-colors"><LogOut className="w-5 h-5" />Salir</button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Panel de Alertas */}
        {resumen.total > 0 && (
          <div className={`rounded-2xl shadow-soft p-6 mb-8 ${resumen.criticas > 0 ? 'bg-rose-50 border-2 border-rose-200' : resumen.urgentes > 0 ? 'bg-amber-50 border-2 border-amber-200' : 'bg-yellow-50 border-2 border-yellow-200'}`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Bell className={`w-8 h-8 ${resumen.criticas > 0 ? 'text-rose-600' : resumen.urgentes > 0 ? 'text-amber-600' : 'text-yellow-600'}`} />
                <div>
                  <h2 className="text-xl font-bold text-slate-900 font-display">Multas Pendientes de Identificación</h2>
                  <p className="text-sm text-slate-600">Plazo máximo: 20 días naturales</p>
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={handleEnviarReporteAlertas} disabled={enviandoReporte} className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-xl hover:from-primary-600 hover:to-primary-700 hover:shadow-glow disabled:opacity-50 transition-all">
                  <Mail className="w-4 h-4" />{enviandoReporte ? 'Enviando...' : 'Enviar Reporte'}
                </button>
                <button onClick={handleDescargarExcelCompleto} className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-xl hover:from-emerald-600 hover:to-emerald-700 transition-all">
                  <Download className="w-4 h-4" />Excel Completo
                </button>
              </div>
            </div>
            <div className="grid grid-cols-4 gap-4 mb-4">
              <div className="bg-white rounded-xl p-4 text-center border-2 border-rose-200 hover:shadow-soft transition-all">
                <div className="text-3xl font-bold text-rose-600">{resumen.criticas}</div>
                <div className="text-sm text-slate-600">Críticas (18-20d)</div>
              </div>
              <div className="bg-white rounded-xl p-4 text-center border-2 border-amber-200 hover:shadow-soft transition-all">
                <div className="text-3xl font-bold text-amber-600">{resumen.urgentes}</div>
                <div className="text-sm text-slate-600">Urgentes (15-17d)</div>
              </div>
              <div className="bg-white rounded-xl p-4 text-center border-2 border-yellow-200 hover:shadow-soft transition-all">
                <div className="text-3xl font-bold text-yellow-600">{resumen.atencion}</div>
                <div className="text-sm text-slate-600">Atención (11-14d)</div>
              </div>
              <div className="bg-white rounded-xl p-4 text-center border-2 border-emerald-200 hover:shadow-soft transition-all">
                <div className="text-3xl font-bold text-emerald-600">{resumen.normales}</div>
                <div className="text-sm text-slate-600">Normal (0-10d)</div>
              </div>
            </div>
            {multasPendientes.length > 0 && (
              <div className="bg-white rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="py-2 px-3 text-left text-slate-700">Estado</th>
                      <th className="py-2 px-3 text-left text-slate-700">Matrícula</th>
                      <th className="py-2 px-3 text-left text-slate-700">Expediente</th>
                      <th className="py-2 px-3 text-left text-slate-700">Empresa</th>
                      <th className="py-2 px-3 text-left text-slate-700">Organismo</th>
                      <th className="py-2 px-3 text-center text-slate-700">Días</th>
                      <th className="py-2 px-3 text-center text-slate-700">Restantes</th>
                      <th className="py-2 px-3 text-left text-slate-700">Fecha Límite</th>
                    </tr>
                  </thead>
                  <tbody>
                    {multasPendientes.slice(0, 10).map((m, i) => (
                      <tr key={i} className={m.urgencia === 'CRITICO' ? 'bg-rose-50' : m.urgencia === 'URGENTE' ? 'bg-amber-50' : m.urgencia === 'ATENCION' ? 'bg-yellow-50' : 'bg-emerald-50'}>
                        <td className="py-2 px-3 font-bold" style={{ color: m.color }}>{m.emoji} {m.urgencia}</td>
                        <td className="py-2 px-3 text-slate-900">{m.matricula || '-'}</td>
                        <td className="py-2 px-3 text-slate-900">{m.numero_expediente || '-'}</td>
                        <td className="py-2 px-3 text-slate-900">{m.empresa_nombre || '-'}</td>
                        <td className="py-2 px-3 text-slate-900">{m.organismo_emisor || '-'}</td>
                        <td className="py-2 px-3 text-center font-bold" style={{ color: m.color }}>{m.dias_transcurridos}</td>
                        <td className="py-2 px-3 text-center font-bold" style={{ color: m.dias_restantes <= 5 ? '#e11d48' : '#334155' }}>{m.dias_restantes}</td>
                        <td className="py-2 px-3 text-slate-900">{m.fecha_limite}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {multasPendientes.length > 10 && (
                  <div className="p-3 text-center bg-slate-50">
                    <button onClick={() => navigate('/admin/multas/pendientes')} className="text-primary-600 hover:text-primary-700 font-medium transition-colors">
                      Ver todas ({multasPendientes.length} multas pendientes)
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* === ACCIONES MANUALES PRIORITARIAS === */}
        {accionesManuales && accionesManuales.resumen.total_acciones_pendientes > 0 && (
          <div className="relative rounded-2xl shadow-soft mb-8 overflow-hidden border-2 border-rose-200 bg-gradient-to-br from-rose-50 via-white to-amber-50">
            {/* Header */}
            <div className="flex items-start justify-between p-6 pb-4 border-b border-rose-100">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-xl bg-rose-100 text-rose-600">
                  <Zap className="w-7 h-7" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-900 font-display">
                    Acciones Manuales Prioritarias
                  </h2>
                  <p className="text-sm text-slate-600">
                    Multas bloqueadas que requieren intervención humana.
                    <span className="text-slate-400 text-xs ml-2">
                      Actualizado {new Date(accionesManuales.resumen.generado_en).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={cargarAccionesManuales}
                  className="p-2 text-slate-500 hover:bg-white hover:shadow-soft rounded-lg transition-all"
                  title="Recargar"
                >
                  <RefreshCw className={`w-4 h-4 ${accionesLoading ? 'animate-spin' : ''}`} />
                </button>
              </div>
            </div>

            {/* KPI revenue bloqueado */}
            <div className="px-6 py-4 bg-gradient-to-r from-rose-100/60 via-rose-50/40 to-transparent border-b border-rose-100 flex items-center gap-6">
              <div className="flex items-center gap-3">
                <TrendingDown className="w-5 h-5 text-rose-600" />
                <span className="text-sm font-medium text-slate-700">Revenue bloqueado:</span>
              </div>
              <div className="text-3xl font-bold text-rose-700 font-display">
                {accionesManuales.resumen.revenue_bloqueado.toLocaleString('es-ES', { minimumFractionDigits: 2 })} €
              </div>
              <div className="flex-1" />
              <div className="text-sm text-slate-600">
                <span className="font-bold text-slate-900">{accionesManuales.resumen.total_acciones_pendientes}</span> multas en cola
              </div>
            </div>

            {/* 4 buckets */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 p-6">
              <BucketCard
                icon={Wrench}
                label={accionesManuales.buckets.revision_manual.label}
                descripcion={accionesManuales.buckets.revision_manual.descripcion}
                count={accionesManuales.buckets.revision_manual.count}
                importe={accionesManuales.buckets.revision_manual.importe_total}
                diasPromedio={accionesManuales.buckets.revision_manual.dias_promedio}
                color="amber"
                onClick={() => setBucketExpandido(bucketExpandido === 'revision_manual' ? null : 'revision_manual')}
                expandido={bucketExpandido === 'revision_manual'}
                muestra={accionesManuales.buckets.revision_manual.muestra}
                onNavigate={(id) => navigate(`/admin/multas/${id}`)}
              />
              <BucketCard
                icon={XCircle}
                label={accionesManuales.buckets.error_identificacion.label}
                descripcion={accionesManuales.buckets.error_identificacion.descripcion}
                count={accionesManuales.buckets.error_identificacion.count}
                importe={accionesManuales.buckets.error_identificacion.importe_total}
                diasPromedio={accionesManuales.buckets.error_identificacion.dias_promedio}
                color="rose"
                onClick={() => setBucketExpandido(bucketExpandido === 'error_identificacion' ? null : 'error_identificacion')}
                expandido={bucketExpandido === 'error_identificacion'}
                muestra={accionesManuales.buckets.error_identificacion.muestra}
                onNavigate={(id) => navigate(`/admin/multas/${id}`)}
              />
              <BucketCard
                icon={PenSquare}
                label={accionesManuales.buckets.madrid_firma.label}
                descripcion={accionesManuales.buckets.madrid_firma.descripcion}
                count={accionesManuales.buckets.madrid_firma.count}
                importe={accionesManuales.buckets.madrid_firma.importe_total}
                diasPromedio={accionesManuales.buckets.madrid_firma.dias_promedio}
                color="violet"
                onClick={() => setBucketExpandido(bucketExpandido === 'madrid_firma' ? null : 'madrid_firma')}
                expandido={bucketExpandido === 'madrid_firma'}
                muestra={accionesManuales.buckets.madrid_firma.muestra}
                onNavigate={(id) => navigate(`/admin/multas/${id}`)}
              />
              <BucketCard
                icon={UserX}
                label={accionesManuales.buckets.datos_faltantes.label}
                descripcion={accionesManuales.buckets.datos_faltantes.descripcion}
                count={accionesManuales.buckets.datos_faltantes.count}
                importe={accionesManuales.buckets.datos_faltantes.importe_total}
                diasPromedio={accionesManuales.buckets.datos_faltantes.dias_promedio}
                color="sky"
                onClick={() => setBucketExpandido(bucketExpandido === 'datos_faltantes' ? null : 'datos_faltantes')}
                expandido={bucketExpandido === 'datos_faltantes'}
                muestra={accionesManuales.buckets.datos_faltantes.muestra}
                onNavigate={(id) => navigate(`/admin/multas/${id}`)}
              />
            </div>

            {/* Top empresas + CTA */}
            {accionesManuales.top_empresas && accionesManuales.top_empresas.length > 0 && (
              <div className="px-6 pb-6">
                <div className="bg-white/70 backdrop-blur rounded-xl p-4 border border-slate-200">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
                      Top empresas afectadas
                    </h3>
                    {/* Antes iba a /admin/multas?estado=requires_revision_manual,
                        que devuelve 400: ese estado no esta en el enum Joi del
                        listado. Ahora va a la lista priorizada por plazo. */}
                    <button
                      onClick={() => navigate('/admin/multas/no-comunicables')}
                      className="text-sm font-medium text-rose-600 hover:text-rose-700 flex items-center gap-1 transition-colors"
                    >
                      Ver todas <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="space-y-2">
                    {accionesManuales.top_empresas.slice(0, 3).map((emp) => (
                      <div
                        key={emp.id}
                        onClick={() => navigate(`/admin/multas/no-comunicables?empresa_id=${emp.id}`)}
                        className="flex items-center justify-between p-3 rounded-lg hover:bg-rose-50 cursor-pointer transition-colors group"
                      >
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <Building2 className="w-4 h-4 text-slate-400 flex-shrink-0" />
                          <span className="font-medium text-slate-900 truncate">{emp.nombre}</span>
                        </div>
                        <div className="flex items-center gap-4 text-sm flex-shrink-0">
                          <span className="text-slate-600">
                            <span className="font-bold text-slate-900">{emp.total}</span> multas
                          </span>
                          <span className="font-bold text-rose-600">
                            {parseFloat(emp.importe).toLocaleString('es-ES', { minimumFractionDigits: 2 })} €
                          </span>
                          <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-rose-600 group-hover:translate-x-0.5 transition-all" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 mb-8">
          <StatCard icon={FileText} title="Total Multas" value={global.total_multas || 0} subtitle="Este mes" color="primary" onClick={() => navigate('/admin/multas')} />
          {/* Automáticas = comunicadas al organismo, que es lo que factura a 4 €.
              Antes apuntaba a conductor_identificado y no cuadraba con la tarifa. */}
          <StatCard icon={CheckCircle} title="Automáticas" value={global.automaticas || 0} subtitle="Comunicadas (4€)" color="emerald" onClick={() => navigate('/admin/multas?estado=comunicado_organismo')} />
          <StatCard icon={AlertCircle} title="Manuales" value={global.manuales || 0} subtitle="ID manual (6€)" color="amber" onClick={() => navigate('/admin/multas?estado=pendiente_identificacion')} />
          <StatCard icon={Send} title="Comunicadas" value={global.comunicadas || 0} subtitle="Enviadas al organismo" color="primary" onClick={() => navigate('/admin/multas?estado=comunicado_organismo')} />
          <StatCard icon={DollarSign} title="A Facturar" value={`${global.total_facturado || 0}€`} subtitle="Solo comunicadas" color="violet" onClick={() => navigate('/admin/multas?estado=comunicado_organismo')} />
          <StatCard
            icon={TrendingDown}
            title="Revenue Bloqueado"
            value={accionesManuales ? `${Math.round(accionesManuales.resumen.revenue_bloqueado).toLocaleString('es-ES')}€` : '—'}
            subtitle={accionesManuales ? `${accionesManuales.resumen.total_acciones_pendientes} multas atascadas` : 'cargando…'}
            color="rose"
            onClick={() => navigate('/admin/multas/no-comunicables')}
          />
        </div>

        {/* Tendencia últimos N días */}
        {tendencia && tendencia.serie && tendencia.serie.length > 0 && (
          <div className="stat-card border border-slate-200 mb-8">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-xl bg-primary-100 text-primary-600">
                  <BarChart3 className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-900 font-display">Tendencia de actividad</h2>
                  <p className="text-sm text-slate-600">
                    <span className="font-bold text-slate-900">{tendencia.totales.entradas}</span> entradas ·
                    <span className="font-bold text-emerald-600 ml-1">{tendencia.totales.identificadas}</span> identificadas ·
                    <span className="font-bold text-violet-600 ml-1">{tendencia.totales.comunicadas}</span> comunicadas
                    <span className="text-slate-400 ml-2">en los últimos {tendencia.dias} días</span>
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
                {[7, 30, 60, 90].map((d) => (
                  <button
                    key={d}
                    onClick={() => setTendenciaDias(d)}
                    className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${
                      tendenciaDias === d
                        ? 'bg-white text-primary-700 shadow-sm'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    {d}d
                  </button>
                ))}
              </div>
            </div>
            <TrendChart serie={tendencia.serie} />
          </div>
        )}

        {/* Completitud Documental */}
        {dashboard?.documentos && (
          <div className="stat-card border border-slate-200 mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 rounded-xl bg-sky-100 text-sky-600"><FileCheck className="w-6 h-6" /></div>
              <div>
                <h2 className="text-xl font-bold text-slate-900 font-display">Completitud Documental</h2>
                <p className="text-sm text-slate-600">Multas comunicadas con documentación completa (3/3)</p>
              </div>
            </div>
            {(() => {
              const docs = dashboard.documentos;
              const total = parseInt(docs.comunicadas_total) || 0;
              const completos = parseInt(docs.docs_completos) || 0;
              const pct = total > 0 ? Math.round((completos / total) * 100) : 0;
              return (
                <>
                  <div className="flex items-center gap-4 mb-4 cursor-pointer" onClick={() => { navigate('/admin/multas?docs=completos') }} title="Ver multas con documentación completa">
                    <div className="flex-1 bg-slate-100 rounded-full h-4 overflow-hidden">
                      <div className={`h-full rounded-full transition-all ${pct === 100 ? 'bg-emerald-500' : pct >= 70 ? 'bg-sky-500' : 'bg-amber-500'}`} style={{ width: `${pct}%` }}></div>
                    </div>
                    <span className={`text-2xl font-bold ${pct === 100 ? 'text-emerald-600' : pct >= 70 ? 'text-sky-600' : 'text-amber-600'}`}>{pct}%</span>
                  </div>
                  <div className="grid grid-cols-4 gap-4">
                    <button onClick={() => { navigate('/admin/multas?docs=comunicadas') }} className="bg-white rounded-xl p-3 text-center border border-slate-200 hover:border-slate-400 hover:shadow-soft hover:-translate-y-0.5 transition-all cursor-pointer">
                      <div className="text-2xl font-bold text-slate-900">{total}</div>
                      <div className="text-xs text-slate-500">Comunicadas</div>
                    </button>
                    <button onClick={() => { navigate('/admin/multas?docs=con_notificacion') }} className="bg-white rounded-xl p-3 text-center border border-sky-200 hover:border-sky-400 hover:shadow-soft hover:-translate-y-0.5 transition-all cursor-pointer">
                      <div className="text-2xl font-bold text-sky-600">{parseInt(docs.con_notificacion) || 0}</div>
                      <div className="text-xs text-slate-500">Con Notificación</div>
                    </button>
                    <button onClick={() => { navigate('/admin/multas?docs=con_escrito') }} className="bg-white rounded-xl p-3 text-center border border-violet-200 hover:border-violet-400 hover:shadow-soft hover:-translate-y-0.5 transition-all cursor-pointer">
                      <div className="text-2xl font-bold text-violet-600">{parseInt(docs.con_escrito) || 0}</div>
                      <div className="text-xs text-slate-500">Con Escrito</div>
                    </button>
                    <button onClick={() => { navigate('/admin/multas?docs=con_justificante') }} className="bg-white rounded-xl p-3 text-center border border-emerald-200 hover:border-emerald-400 hover:shadow-soft hover:-translate-y-0.5 transition-all cursor-pointer">
                      <div className="text-2xl font-bold text-emerald-600">{parseInt(docs.con_justificante) || 0}</div>
                      <div className="text-xs text-slate-500">Con Justificante</div>
                    </button>
                  </div>
                </>
              );
            })()}
          </div>
        )}

        {/* Acciones Rápidas — reagrupadas en 4 secciones temáticas */}
        <div className="stat-card border border-slate-200 mb-8">
          <h2 className="text-xl font-bold text-slate-900 font-display mb-5">Acciones Rápidas</h2>

          {/* Sección 1: Operaciones diarias (Multas) */}
          <SeccionAcciones titulo="Operaciones de multas" icon={FileText}>
            <AccionButton onClick={() => navigate('/admin/multas')} icon={FileText} color="slate" label="Todas las Multas" subtitle={`${global.total_multas || 0} registradas`} />
            <AccionButton onClick={() => navigate('/admin/multas/pendientes')} icon={AlertCircle} color="amber" label="Multas Pendientes" subtitle={`${global.pendientes || 0} sin identificar`} />
            <AccionButton onClick={() => navigate('/subir-multa')} icon={Camera} color="cyan" label="Subir Multa" subtitle="OCR foto/PDF" />
            <AccionButton onClick={() => navigate('/admin/boe-scans')} icon={Newspaper} color="slate" label="BOE Scanner" subtitle="Tablón Edictal" />
          </SeccionAcciones>

          {/* Sección 2: Facturación y reportes */}
          <SeccionAcciones titulo="Facturación y reportes" icon={DollarSign}>
            <AccionButton onClick={() => navigate('/admin/facturacion')} icon={DollarSign} color="violet" label="Facturación" subtitle="Histórico mensual" />
            <AccionButton onClick={handleGenerarReporte} icon={Download} color="primary" label="Descargar Excel" subtitle="Reporte del mes" />
            <AccionButton onClick={handleEnviarReporte} icon={Mail} color="emerald" label="Enviar Reporte" subtitle="Email a dirección" />
            <AccionButton onClick={() => navigate('/admin/recargos')} icon={AlertTriangle} color="rose" label="Recargos" subtitle="ID tardía" />
          </SeccionAcciones>

          {/* Sección 3: Gestión comercial */}
          <SeccionAcciones titulo="Gestión comercial" icon={Users}>
            <AccionButton onClick={() => navigate('/admin/empresas')} icon={Building} color="sky" label="Empresas" subtitle="Clientes activos" />
            <AccionButton onClick={() => navigate('/admin/usuarios')} icon={Users} color="violet" label="Usuarios" subtitle="Gestionar accesos" />
            <AccionButton onClick={() => navigate('/admin/trials')} icon={UserPlus} color="cyan" label="Trials" subtitle="Demos en curso" />
            <AccionButton onClick={() => navigate('/admin/licenciatarios')} icon={Building2} color="emerald" label="Licenciatarios" subtitle="MyBooking toggle" />
          </SeccionAcciones>

          {/* Sección 4: Configuración del sistema */}
          <SeccionAcciones titulo="Configuración" icon={ShieldCheck} ultima>
            <AccionButton onClick={() => navigate('/admin/subarrendatarios')} icon={Building2} color="rose" label="Subarrendatarios" subtitle="Empresas intermedias" />
            <AccionButton onClick={() => navigate('/admin/certificados')} icon={ShieldCheck} color="primary" label="Certificados" subtitle="DGT digital" />
          </SeccionAcciones>
        </div>

        {/* Por Empresa */}
        <div className="stat-card border border-slate-200">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-slate-900 font-display">Por Empresa</h2>
            <span className="text-xs text-slate-500">
              {empresas.length} {empresas.length === 1 ? 'empresa' : 'empresas'} con multas
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-4 font-medium text-slate-700">Empresa</th>
                  <th className="text-center py-3 px-4 font-medium text-slate-700">Total</th>
                  <th className="text-center py-3 px-4 font-medium text-slate-700">Automáticas</th>
                  <th className="text-center py-3 px-4 font-medium text-slate-700">Manuales</th>
                  <th className="text-center py-3 px-4 font-medium text-slate-700">Pendientes</th>
                  <th className="text-center py-3 px-4 font-medium text-slate-700">Error</th>
                  {/* Faltaban: las columnas cubrian 5 estados de 8 y en IBERFURGO
                      dejaban 337 multas sin aparecer en ninguna. */}
                  <th className="text-center py-3 px-4 font-medium text-slate-700" title="Apartadas para revisión manual">Revisión</th>
                  <th className="text-center py-3 px-4 font-medium text-slate-700" title="Gestionadas fuera del sistema">Externas</th>
                  {/* Estaba en el SQL pero no se pintaba: por eso la fila seguia
                      sin sumar el total (faltaban 5 en IBERFURGO). */}
                  <th className="text-center py-3 px-4 font-medium text-slate-700" title="Sanciones de pago pendientes">Pago</th>
                  {/* El organismo cerró el expediente: no es un error nuestro
                      y no se puede identificar. Se ve, pero aparte. */}
                  <th className="text-center py-3 px-4 font-medium text-slate-500" title="El organismo cerró el expediente: ya no admite identificación. No es un fallo del sistema y no se reintenta.">Cerradas</th>
                  <th className="text-center py-3 px-4 font-medium text-violet-700">Comunicadas</th>
                  {/* "A Cobrar" mostraba en realidad lo ya marcado como facturado
                      en el mes en curso. Ahora se dice lo que es, y al lado va lo
                      que de verdad falta por cobrar. */}
                  {/* La cifra honesta: 4€ x comunicada + 6€ x manual, ignorando
                      el importe guardado. Si no coincide con "Facturado", hay
                      importes mal grabados. */}
                  <th className="text-right py-3 px-4 font-medium text-amber-700" title="Lo que sale de aplicar la tarifa acordada: 4 € por comunicada automática y 6 € por manual">Según tarifa</th>
                  <th className="text-right py-3 px-4 font-medium text-slate-600" title="Ya marcado como facturado con periodo del mes en curso">Facturado (mes)</th>
                  <th className="text-right py-3 px-4 font-medium text-violet-700" title="Comunicadas al organismo y todavía sin marcar como facturadas">Pendiente de facturar</th>
                  <th className="text-center py-3 px-4 font-medium text-slate-700 w-1">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {empresas.map((empresa) => (
                  <tr
                    key={empresa.id}
                    className="border-b border-slate-100 hover:bg-slate-50 transition-colors group cursor-pointer"
                    onClick={() => navigate(`/admin/multas?empresa_id=${empresa.id}`)}
                  >
                    <td className="py-3 px-4 font-medium text-slate-900">{empresa.nombre}</td>
                    <td className="py-3 px-4 text-center text-slate-900">{empresa.total_multas}</td>
                    <td className="py-3 px-4 text-center text-emerald-600 font-medium">{empresa.automaticas}</td>
                    <td className="py-3 px-4 text-center text-amber-600 font-medium">{empresa.manuales}</td>
                    <td className="py-3 px-4 text-center text-rose-600 font-medium">{empresa.pendientes}</td>
                    <td className="py-3 px-4 text-center text-red-600 font-medium">{empresa.con_error || 0}</td>
                    <td className="py-3 px-4 text-center text-amber-600 font-medium">{empresa.revision_manual || 0}</td>
                    <td className="py-3 px-4 text-center text-slate-500">{empresa.gestionada_externa || 0}</td>
                    <td className="py-3 px-4 text-center text-slate-500">{empresa.pendiente_pago || 0}</td>
                    {empresa.sin_clasificar > 0 && (
                      <td className="py-3 px-4 text-center text-rose-700 font-bold"
                          title="Multas en un estado que no tiene columna. Avisa para poder añadirla.">
                        {empresa.sin_clasificar} ⚠
                      </td>
                    )}
                    <td className="py-3 px-4 text-center text-slate-500">{empresa.cerradas_organismo || 0}</td>
                    <td className="py-3 px-4 text-center text-violet-600 font-bold">{empresa.comunicadas || 0}</td>
                    <td className="py-3 px-4 text-right font-bold text-amber-700">
                      {Number(empresa.total_segun_tarifa || 0).toFixed(2)} €
                    </td>
                    <td className="py-3 px-4 text-right text-slate-600">
                      {Number(empresa.facturado_mes_actual ?? empresa.total_a_cobrar ?? 0).toFixed(2)} €
                    </td>
                    <td className="py-3 px-4 text-right font-bold text-violet-700">
                      {Number(empresa.pendiente_de_facturar || 0).toFixed(2)} €
                      {empresa.multas_pendientes_facturar > 0 && (
                        <div className="text-xs font-normal text-slate-500">
                          {empresa.multas_pendientes_facturar} multas
                        </div>
                      )}
                    </td>
                    <td className="py-3 px-2" onClick={(e) => e.stopPropagation()}>
                      <AccionesEmpresaMenu empresa={empresa} navigate={navigate} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}

function StatCard({ icon: Icon, title, value, subtitle, color, onClick }) {
  const colors = {
    primary: 'bg-primary-100 text-primary-600',
    emerald: 'bg-emerald-100 text-emerald-600',
    amber: 'bg-amber-100 text-amber-600',
    violet: 'bg-violet-100 text-violet-600',
    rose: 'bg-rose-100 text-rose-600',
    sky: 'bg-sky-100 text-sky-600',
  };
  return (
    <div className={`stat-card border border-slate-200 ${onClick ? 'cursor-pointer hover:shadow-soft hover:-translate-y-0.5 transition-all' : ''}`} onClick={onClick}>
      <div className="flex items-center justify-between mb-4">
        <div className={`p-3 rounded-xl ${colors[color]}`}><Icon className="w-6 h-6" /></div>
      </div>
      <h3 className="text-slate-600 text-sm font-medium mb-1">{title}</h3>
      <p className="text-3xl font-bold text-slate-900 mb-1">{value}</p>
      <p className="text-sm text-slate-500">{subtitle}</p>
    </div>
  );
}

/**
 * Menú de acciones contextuales por empresa en la tabla "Por Empresa".
 * Botón ⋯ con dropdown: ver multas / ver pendientes / ver error / facturación / certificados.
 */
function AccionesEmpresaMenu({ empresa, navigate }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const items = [
    {
      label: 'Ver todas las multas',
      icon: FileText,
      onClick: () => navigate(`/admin/multas?empresa_id=${empresa.id}`),
      color: 'text-slate-700',
    },
    {
      label: `Pendientes (${empresa.pendientes || 0})`,
      icon: AlertCircle,
      onClick: () => navigate(`/admin/multas?empresa_id=${empresa.id}&estado=pendiente_identificacion`),
      color: 'text-amber-700',
      disabled: !empresa.pendientes,
    },
    {
      label: `Con error (${empresa.con_error || 0})`,
      icon: XCircle,
      onClick: () => navigate(`/admin/multas?empresa_id=${empresa.id}&estado=error_identificacion`),
      color: 'text-rose-700',
      disabled: !empresa.con_error,
    },
    {
      label: `Comunicadas (${empresa.comunicadas || 0})`,
      icon: Send,
      onClick: () => navigate(`/admin/multas?empresa_id=${empresa.id}&estado=comunicado_organismo`),
      color: 'text-violet-700',
      disabled: !empresa.comunicadas,
    },
    { divider: true },
    {
      label: 'Certificados DGT',
      icon: ShieldCheck,
      onClick: () => navigate(`/admin/certificados?empresa_id=${empresa.id}`),
      color: 'text-primary-700',
    },
    {
      label: 'Editar empresa',
      icon: Building,
      onClick: () => navigate(`/admin/empresas?id=${empresa.id}`),
      color: 'text-sky-700',
    },
  ];

  return (
    <div ref={containerRef} className="relative inline-block">
      <button
        onClick={() => setOpen(!open)}
        className="p-1.5 rounded-lg text-slate-400 hover:text-slate-900 hover:bg-slate-100 transition-all"
        title="Acciones"
      >
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <circle cx="5" cy="12" r="2" />
          <circle cx="12" cy="12" r="2" />
          <circle cx="19" cy="12" r="2" />
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 w-56 bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden z-40">
          {items.map((item, i) =>
            item.divider ? (
              <div key={i} className="h-px bg-slate-100 my-1" />
            ) : (
              <button
                key={i}
                onClick={() => {
                  item.onClick();
                  setOpen(false);
                }}
                disabled={item.disabled}
                className={`w-full flex items-center gap-3 px-3 py-2 text-sm hover:bg-slate-50 transition-colors text-left ${item.color} ${item.disabled ? 'opacity-40 cursor-not-allowed hover:bg-transparent' : ''}`}
              >
                <item.icon className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">{item.label}</span>
              </button>
            )
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Gráfico de líneas SVG nativo (sin recharts) para mostrar tendencia 30/60/90 días
 * de entradas / identificadas / comunicadas. Tooltip al hover sobre cada punto del eje X.
 */
function TrendChart({ serie }) {
  const [hoverIdx, setHoverIdx] = useState(null);
  const ref = useRef(null);

  const W = 800; // viewBox width (escalará al contenedor)
  const H = 220;
  const padL = 36;
  const padR = 12;
  const padT = 12;
  const padB = 28;
  const chartW = W - padL - padR;
  const chartH = H - padT - padB;

  // Valor máximo entre las 3 series (para escalar Y)
  const maxY = Math.max(
    10,
    ...serie.flatMap((d) => [d.entradas, d.identificadas, d.comunicadas])
  );

  const xFor = (i) => padL + (serie.length === 1 ? chartW / 2 : (i / (serie.length - 1)) * chartW);
  const yFor = (v) => padT + chartH - (v / maxY) * chartH;

  // Polyline points
  const linePoints = (key) => serie.map((d, i) => `${xFor(i)},${yFor(d[key])}`).join(' ');

  // Polyline área (con baseline)
  const areaPoints = (key) =>
    `${padL},${padT + chartH} ${serie.map((d, i) => `${xFor(i)},${yFor(d[key])}`).join(' ')} ${padL + chartW},${padT + chartH}`;

  // Ticks Y (5 divisiones)
  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((t) => Math.round(maxY * t));

  // Mouse handler — usa coordenadas relativas al viewBox
  const handleMouseMove = (e) => {
    const svg = ref.current;
    if (!svg) return;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const ctm = svg.getScreenCTM();
    if (!ctm) return;
    const loc = pt.matrixTransform(ctm.inverse());
    if (loc.x < padL || loc.x > padL + chartW) {
      setHoverIdx(null);
      return;
    }
    const idx = Math.round(((loc.x - padL) / chartW) * (serie.length - 1));
    setHoverIdx(Math.max(0, Math.min(serie.length - 1, idx)));
  };

  const hoverD = hoverIdx != null ? serie[hoverIdx] : null;
  const formatDate = (s) => {
    if (!s) return '';
    const d = new Date(s);
    return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
  };

  return (
    <div className="relative">
      <svg
        ref={ref}
        viewBox={`0 0 ${W} ${H}`}
        className="w-full h-auto select-none"
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setHoverIdx(null)}
      >
        <defs>
          <linearGradient id="gradEntradas" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#0ea5e9" stopOpacity="0.25" />
            <stop offset="100%" stopColor="#0ea5e9" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="gradIdentif" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#10b981" stopOpacity="0.25" />
            <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="gradComunic" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#8b5cf6" stopOpacity="0.25" />
            <stop offset="100%" stopColor="#8b5cf6" stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* Grid horizontal */}
        {yTicks.map((v, i) => (
          <g key={i}>
            <line
              x1={padL}
              x2={padL + chartW}
              y1={yFor(v)}
              y2={yFor(v)}
              stroke="#e2e8f0"
              strokeDasharray={i === 0 ? '' : '2 4'}
            />
            <text x={padL - 8} y={yFor(v) + 4} fontSize="10" fill="#94a3b8" textAnchor="end">
              {v}
            </text>
          </g>
        ))}

        {/* Eje X — solo primer, último y mitad */}
        {[0, Math.floor(serie.length / 2), serie.length - 1].map((i) => (
          <text
            key={i}
            x={xFor(i)}
            y={H - 8}
            fontSize="10"
            fill="#94a3b8"
            textAnchor="middle"
          >
            {formatDate(serie[i]?.fecha)}
          </text>
        ))}

        {/* Áreas */}
        <polyline points={areaPoints('entradas')} fill="url(#gradEntradas)" stroke="none" />
        <polyline points={areaPoints('identificadas')} fill="url(#gradIdentif)" stroke="none" />
        <polyline points={areaPoints('comunicadas')} fill="url(#gradComunic)" stroke="none" />

        {/* Líneas */}
        <polyline points={linePoints('entradas')} fill="none" stroke="#0ea5e9" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        <polyline points={linePoints('identificadas')} fill="none" stroke="#10b981" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        <polyline points={linePoints('comunicadas')} fill="none" stroke="#8b5cf6" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />

        {/* Hover guide + puntos */}
        {hoverIdx != null && (
          <g pointerEvents="none">
            <line
              x1={xFor(hoverIdx)}
              x2={xFor(hoverIdx)}
              y1={padT}
              y2={padT + chartH}
              stroke="#cbd5e1"
              strokeDasharray="3 3"
            />
            <circle cx={xFor(hoverIdx)} cy={yFor(hoverD.entradas)} r="4" fill="#0ea5e9" stroke="white" strokeWidth="2" />
            <circle cx={xFor(hoverIdx)} cy={yFor(hoverD.identificadas)} r="4" fill="#10b981" stroke="white" strokeWidth="2" />
            <circle cx={xFor(hoverIdx)} cy={yFor(hoverD.comunicadas)} r="4" fill="#8b5cf6" stroke="white" strokeWidth="2" />
          </g>
        )}
      </svg>

      {/* Leyenda */}
      <div className="flex items-center justify-center gap-6 mt-3 text-xs">
        <LeyendaItem color="#0ea5e9" label="Entradas" />
        <LeyendaItem color="#10b981" label="Identificadas" />
        <LeyendaItem color="#8b5cf6" label="Comunicadas" />
      </div>

      {/* Tooltip — overlay HTML, posicionado relativo al contenedor */}
      {hoverD && (
        <div
          className="absolute top-2 right-2 bg-white/95 backdrop-blur shadow-lg rounded-lg px-3 py-2 border border-slate-200 text-xs space-y-1 pointer-events-none"
        >
          <div className="font-bold text-slate-900">{formatDate(hoverD.fecha)}</div>
          <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-sky-500"></span><span className="text-slate-600">Entradas:</span><span className="font-bold text-slate-900 ml-auto">{hoverD.entradas}</span></div>
          <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-emerald-500"></span><span className="text-slate-600">Identificadas:</span><span className="font-bold text-slate-900 ml-auto">{hoverD.identificadas}</span></div>
          <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-violet-500"></span><span className="text-slate-600">Comunicadas:</span><span className="font-bold text-slate-900 ml-auto">{hoverD.comunicadas}</span></div>
        </div>
      )}
    </div>
  );
}

function LeyendaItem({ color, label }) {
  return (
    <div className="flex items-center gap-2 text-slate-600">
      <span className="w-3 h-0.5 rounded-full" style={{ backgroundColor: color }}></span>
      <span>{label}</span>
    </div>
  );
}

/**
 * Búsqueda global en header — autocomplete contra /admin/multas/todas?busqueda=
 * Debounce de 300ms. Muestra 5 resultados con matrícula + expediente + empresa.
 * Click navega a /admin/multas?busqueda=X aplicando el filtro.
 */
function GlobalSearch() {
  const [q, setQ] = useState('');
  const [resultados, setResultados] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  // Debounce
  useEffect(() => {
    if (q.trim().length < 2) {
      setResultados([]);
      return;
    }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.get(`/admin/multas/todas?busqueda=${encodeURIComponent(q.trim())}&limit=5`);
        setResultados(r.data?.data || []);
        setOpen(true);
      } catch (e) {
        setResultados([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  // Cerrar dropdown al click fuera
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Atajo de teclado: '/' enfoca el buscador
  useEffect(() => {
    const handler = (e) => {
      if (e.key === '/' && document.activeElement?.tagName !== 'INPUT' && document.activeElement?.tagName !== 'TEXTAREA') {
        e.preventDefault();
        inputRef.current?.focus();
      }
      if (e.key === 'Escape' && document.activeElement === inputRef.current) {
        inputRef.current?.blur();
        setOpen(false);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (q.trim().length >= 2) {
      navigate(`/admin/multas?busqueda=${encodeURIComponent(q.trim())}`);
      setOpen(false);
      inputRef.current?.blur();
    }
  };

  return (
    <div ref={containerRef} className="relative hidden lg:block w-72">
      <form onSubmit={handleSubmit}>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          <input
            ref={inputRef}
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onFocus={() => resultados.length > 0 && setOpen(true)}
            placeholder="Buscar matrícula, expediente, DNI…"
            className="w-full pl-9 pr-9 py-2 bg-slate-100 hover:bg-slate-50 focus:bg-white border border-transparent focus:border-primary-300 focus:ring-2 focus:ring-primary-100 rounded-xl text-sm text-slate-900 placeholder-slate-400 outline-none transition-all"
          />
          {q && (
            <button
              type="button"
              onClick={() => { setQ(''); setResultados([]); setOpen(false); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
            >
              <X className="w-4 h-4" />
            </button>
          )}
          {!q && (
            <kbd className="absolute right-3 top-1/2 -translate-y-1/2 hidden xl:inline-flex items-center px-1.5 h-5 text-[10px] font-mono text-slate-400 bg-white border border-slate-200 rounded">
              /
            </kbd>
          )}
        </div>
      </form>

      {open && (loading || resultados.length > 0 || q.trim().length >= 2) && (
        <div className="absolute top-full mt-2 left-0 right-0 bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden z-50">
          {loading && (
            <div className="px-4 py-3 text-sm text-slate-500 flex items-center gap-2">
              <RefreshCw className="w-4 h-4 animate-spin" />
              Buscando…
            </div>
          )}
          {!loading && resultados.length === 0 && q.trim().length >= 2 && (
            <div className="px-4 py-3 text-sm text-slate-500">Sin resultados para "{q}"</div>
          )}
          {!loading && resultados.length > 0 && (
            <>
              {resultados.map((m) => (
                <button
                  key={m.id}
                  onClick={() => {
                    navigate(`/admin/multas?busqueda=${encodeURIComponent(m.matricula || m.numero_expediente || '')}`);
                    setOpen(false);
                    setQ('');
                  }}
                  className="w-full text-left px-4 py-3 hover:bg-slate-50 border-b border-slate-100 last:border-b-0 flex items-center gap-3 group"
                >
                  <div className="font-mono text-sm font-bold text-slate-900 w-20 flex-shrink-0">
                    {m.matricula || '—'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate">{m.empresa_nombre || 'Sin empresa'}</div>
                    <div className="text-xs text-slate-500 truncate">
                      Exp: {m.numero_expediente || '—'} · {m.organismo_emisor || '—'}
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-primary-600 group-hover:translate-x-0.5 transition-all flex-shrink-0" />
                </button>
              ))}
              <button
                onClick={handleSubmit}
                className="w-full text-center px-4 py-2.5 bg-slate-50 hover:bg-primary-50 text-sm font-medium text-primary-700 transition-colors"
              >
                Ver todos los resultados →
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Sección agrupada dentro de "Acciones Rápidas". Cada sección tiene un título
 * pequeño en mayúsculas y un grid responsive con sus botones.
 */
function SeccionAcciones({ titulo, icon: Icon, children, ultima }) {
  return (
    <div className={ultima ? '' : 'mb-6'}>
      <div className="flex items-center gap-2 mb-3 text-slate-500">
        <Icon className="w-4 h-4" />
        <h3 className="text-xs font-bold uppercase tracking-wider">{titulo}</h3>
        <div className="flex-1 h-px bg-slate-200" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {children}
      </div>
    </div>
  );
}

/**
 * Botón individual de acción. Variante reducida del botón legacy.
 */
function AccionButton({ onClick, icon: Icon, color, label, subtitle }) {
  const colors = {
    slate: { border: 'border-slate-200', hover: 'hover:bg-slate-50', text: 'text-slate-600' },
    primary: { border: 'border-primary-200', hover: 'hover:bg-primary-50', text: 'text-primary-600' },
    emerald: { border: 'border-emerald-200', hover: 'hover:bg-emerald-50', text: 'text-emerald-600' },
    amber: { border: 'border-amber-200', hover: 'hover:bg-amber-50', text: 'text-amber-600' },
    violet: { border: 'border-violet-200', hover: 'hover:bg-violet-50', text: 'text-violet-600' },
    rose: { border: 'border-rose-200', hover: 'hover:bg-rose-50', text: 'text-rose-600' },
    sky: { border: 'border-sky-200', hover: 'hover:bg-sky-50', text: 'text-sky-600' },
    cyan: { border: 'border-cyan-200', hover: 'hover:bg-cyan-50', text: 'text-cyan-600' },
    accent: { border: 'border-accent-200', hover: 'hover:bg-accent-50', text: 'text-accent-600' },
  };
  const c = colors[color] || colors.slate;
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-3 p-3 border-2 ${c.border} rounded-xl ${c.hover} hover:-translate-y-0.5 hover:shadow-soft transition-all text-left`}
    >
      <Icon className={`w-5 h-5 ${c.text} flex-shrink-0`} />
      <div className="min-w-0 flex-1">
        <p className="font-medium text-slate-900 text-sm truncate">{label}</p>
        <p className="text-xs text-slate-500 truncate">{subtitle}</p>
      </div>
    </button>
  );
}

/**
 * Indicador semafórico de salud del sistema en el header.
 * Verde (<5K€ bloqueado y sin críticas), Ámbar (5K-20K o críticas≤10),
 * Rojo (>20K o críticas>10).
 */
function HealthIndicator({ accionesManuales, resumenAlertas, loading }) {
  if (loading || !accionesManuales) {
    return (
      <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-100 text-slate-500 text-sm">
        <div className="w-2 h-2 rounded-full bg-slate-400 animate-pulse" />
        <span>Cargando salud…</span>
      </div>
    );
  }
  const revenue = accionesManuales.resumen.revenue_bloqueado || 0;
  const criticas = resumenAlertas?.criticas || 0;

  let estado;
  if (revenue > 20000 || criticas > 10) {
    estado = { label: 'Atención requerida', dot: 'bg-rose-500', bg: 'bg-rose-100', text: 'text-rose-700', border: 'border-rose-200', pulse: true };
  } else if (revenue > 5000 || criticas > 0) {
    estado = { label: 'Vigilar', dot: 'bg-amber-500', bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200', pulse: false };
  } else {
    estado = { label: 'Sistema sano', dot: 'bg-emerald-500', bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-200', pulse: false };
  }

  return (
    <div
      className={`hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full ${estado.bg} ${estado.text} text-sm font-medium border ${estado.border}`}
      title={`${criticas} críticas · ${Math.round(revenue).toLocaleString('es-ES')}€ bloqueados`}
    >
      <div className="relative flex items-center justify-center">
        <div className={`w-2 h-2 rounded-full ${estado.dot}`} />
        {estado.pulse && (
          <div className={`absolute w-2 h-2 rounded-full ${estado.dot} animate-ping opacity-75`} />
        )}
      </div>
      <span>{estado.label}</span>
    </div>
  );
}

/**
 * Tarjeta individual de bucket en "Acciones Manuales Prioritarias".
 * Si count=0 se muestra en modo apagado.
 * Click → expande mostrando muestra de 5 multas.
 */
function BucketCard({ icon: Icon, label, descripcion, count, importe, diasPromedio, color, onClick, expandido, muestra, onNavigate }) {
  const colors = {
    amber: {
      bg: 'bg-amber-50',
      bgIcon: 'bg-amber-100',
      text: 'text-amber-700',
      textIcon: 'text-amber-600',
      border: count > 0 ? 'border-amber-200' : 'border-slate-200',
      hover: 'hover:border-amber-400 hover:bg-amber-50',
    },
    rose: {
      bg: 'bg-rose-50',
      bgIcon: 'bg-rose-100',
      text: 'text-rose-700',
      textIcon: 'text-rose-600',
      border: count > 0 ? 'border-rose-200' : 'border-slate-200',
      hover: 'hover:border-rose-400 hover:bg-rose-50',
    },
    violet: {
      bg: 'bg-violet-50',
      bgIcon: 'bg-violet-100',
      text: 'text-violet-700',
      textIcon: 'text-violet-600',
      border: count > 0 ? 'border-violet-200' : 'border-slate-200',
      hover: 'hover:border-violet-400 hover:bg-violet-50',
    },
    sky: {
      bg: 'bg-sky-50',
      bgIcon: 'bg-sky-100',
      text: 'text-sky-700',
      textIcon: 'text-sky-600',
      border: count > 0 ? 'border-sky-200' : 'border-slate-200',
      hover: 'hover:border-sky-400 hover:bg-sky-50',
    },
  };
  const c = colors[color] || colors.amber;
  const apagado = count === 0;

  return (
    <div className={`bg-white rounded-xl border-2 ${c.border} transition-all ${apagado ? 'opacity-60' : c.hover} ${expandido ? 'lg:col-span-4 md:col-span-2' : ''}`}>
      <button
        onClick={onClick}
        disabled={apagado}
        className={`w-full text-left p-4 ${apagado ? 'cursor-not-allowed' : 'cursor-pointer'}`}
      >
        <div className="flex items-start gap-3 mb-3">
          <div className={`p-2 rounded-lg ${apagado ? 'bg-slate-100 text-slate-400' : `${c.bgIcon} ${c.textIcon}`}`}>
            <Icon className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-slate-900 text-sm leading-tight">{label}</h4>
            <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{descripcion}</p>
          </div>
        </div>
        <div className="flex items-baseline gap-2 mb-1">
          <span className={`text-3xl font-bold ${apagado ? 'text-slate-400' : c.text}`}>{count}</span>
          <span className="text-xs text-slate-500">{count === 1 ? 'multa' : 'multas'}</span>
        </div>
        <div className="flex items-center justify-between text-xs">
          <span className={`font-medium ${apagado ? 'text-slate-400' : 'text-slate-700'}`}>
            {importe.toLocaleString('es-ES', { minimumFractionDigits: 2 })} €
          </span>
          {!apagado && diasPromedio > 0 && (
            <span className="text-slate-500 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              avg {diasPromedio} d
            </span>
          )}
        </div>
      </button>

      {/* Detalle expandido — muestra de 5 multas */}
      {expandido && muestra && muestra.length > 0 && (
        <div className="border-t border-slate-200 bg-slate-50/50 p-3 rounded-b-xl">
          <div className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2 px-1">
            Últimas {muestra.length} multas de este bucket
          </div>
          <div className="space-y-1.5">
            {muestra.map((m) => (
              <div
                key={m.id}
                onClick={() => onNavigate(m.id)}
                className="flex items-center gap-3 p-2 bg-white rounded-lg hover:shadow-soft cursor-pointer transition-all text-sm group"
              >
                <div className="font-mono text-xs text-slate-500 w-16">#{m.id}</div>
                <div className="font-semibold text-slate-900 w-20 font-mono">{m.matricula || '—'}</div>
                <div className="text-slate-700 flex-1 truncate min-w-0">
                  <span className="font-medium">{m.empresa_nombre || '—'}</span>
                  {m.organismo_emisor && <span className="text-slate-400 text-xs ml-2">{m.organismo_emisor}</span>}
                </div>
                <div className="font-bold text-slate-900 flex-shrink-0">
                  {parseFloat(m.importe_multa || 0).toLocaleString('es-ES', { minimumFractionDigits: 2 })} €
                </div>
                <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-slate-700 group-hover:translate-x-0.5 transition-all" />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminDashboard;
